<template>
  <div>
    <h1  v-if="category" class="display-1">
      {{ category.name }}
    </h1>
    <p v-if="searchQuery" style="text-align: left;">
      {{ $t('search.results_for') }} "<b>{{ searchQuery }}</b>"&nbsp;|&nbsp;<a :href="`/category/${route.params.slug}`">clear</a>
    </p>
    <div class="divider-container">
      <el-divider />
    </div>
    <div class="posts-grid">
      <PostCardComponent 
        v-for="edge in posts.edges" 
        :key="edge.node.id" 
        :post="edge.node" 
      />
      <div v-if="posts.pageInfo.hasNextPage && posts.edges.length > 0" class="load-more-container">
        <button @click="loadMorePosts" class="btn btn--primary btn-wide btn--large">
          {{ $t('buttons.load_more') }}
        </button>
      </div>
    </div>
    <EmptyComponent v-if="!loading && posts.edges.length === 0" />
  </div>
</template>

<script>
import PostCardComponent from '@/components/Posts/PostCardComponent.vue';
import EmptyComponent from '@/components/EmptyComponent.vue';

import apolloClient from '@/services/apollo-client';
import { ref, onMounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';
import { GET_CURSOR_PAGINATED_POST_BY_CATEGORY } from '@/graphql/posts';

export default {
  name: 'CategoryPage',
  components: {
    PostCardComponent,
    EmptyComponent,
  },
  setup() {
    const postsPerPage = parseInt(process.env.VUE_APP_POSTS_PER_PAGE) || 12;
    const posts = reactive({ edges: [], pageInfo: { hasNextPage: false, endCursor: null } });
    const loading = ref(false);
    const error = ref(null);
    const category = ref(null);
    const searchQuery = ref('');
    const route = useRoute();

    const fetchPosts = async (first = postsPerPage, after = null, category_slug = "", search = "") => {
      loading.value = true;
      try {
        const { data } = await apolloClient.query({
          query: GET_CURSOR_PAGINATED_POST_BY_CATEGORY,
          variables: { first, after, category_slug, search }
        });

        category.value = data.categoryBySlug;

        if (data.cursorPaginatedPostsByCategory.edges.length === 0) {
          return;
        }

        if (after) {
          posts.edges = [...posts.edges, ...data.cursorPaginatedPostsByCategory.edges];
        } else {
          posts.edges = data.cursorPaginatedPostsByCategory.edges;
        }
        posts.pageInfo = data.cursorPaginatedPostsByCategory.pageInfo;
      } catch (err) {
        error.value = err;
        console.error('Error fetching posts:', err);
      } finally {
        loading.value = false;
      }
    };

    const loadMorePosts = async () => {
      if (posts.pageInfo.hasNextPage) {
        await fetchPosts(postsPerPage, posts.pageInfo.endCursor, route.params.slug, searchQuery.value);
      }
    };

    const parseSearchQuery = () => {
      const params = new URLSearchParams(window.location.search);
      searchQuery.value = params.get('s') || '';
    };

    onMounted(async () => {
      parseSearchQuery();
      await fetchPosts(postsPerPage, null, route.params.slug, searchQuery.value);
    });

    watch(() => route.params.slug, async (newSlug, oldSlug) => {
      if (newSlug !== oldSlug) {
        posts.edges = [];
        posts.pageInfo = { hasNextPage: false, endCursor: null };
        await fetchPosts(postsPerPage, null, newSlug, searchQuery.value);
      }
    });

    return {
      posts,
      loading,
      error,
      route,
      category,
      loadMorePosts,
      searchQuery,
      postsPerPage,
    };
  },
};
</script>

<style scoped>
.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.load-more-container {
  grid-column: 1 / -1;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  position: relative;
  z-index: 1;
}

.load-more-container button {
  width: 100%;
  margin: 0 auto;
}

.divider-container {
  position: relative;
  z-index: 1;
  background: white;
  margin: 20px 0;
}

@media (max-width: 768px) {
  .posts-grid {
    padding: 15px;
    gap: 20px;
  }

  .divider-container {
    position: static;
    z-index: auto;
    margin: 15px 0;
  }

  /* Force all child elements to have no stacking context */
  .divider-container * {
    position: static !important;
    z-index: auto !important;
    transform: none !important;
    will-change: auto !important;
  }
}
</style>
